import { blockDef, type ToolType } from "../engine/blocks";

export const Item = {
  COAL: 256,
  IRON_INGOT: 257,
  GOLD_INGOT: 258,
  DIAMOND: 259,
  WHEAT: 260,
  APPLE: 261,
  BREAD: 262,
  STICK: 263,
  LEATHER: 264,
  RAW_PORK: 265,
  COOKED_PORK: 266,
  SLIME_GEL: 267,

  WOOD_SWORD: 280,
  WOOD_PICK: 281,
  WOOD_AXE: 282,
  WOOD_SHOVEL: 283,
  STONE_SWORD: 284,
  STONE_PICK: 285,
  STONE_AXE: 286,
  STONE_SHOVEL: 287,
  IRON_SWORD: 288,
  IRON_PICK: 289,
  IRON_AXE: 290,
  IRON_SHOVEL: 291,
  GOLD_SWORD: 292,
  GOLD_PICK: 293,
  GOLD_AXE: 294,
  GOLD_SHOVEL: 295,
  DIAMOND_SWORD: 296,
  DIAMOND_PICK: 297,
  DIAMOND_AXE: 298,
  DIAMOND_SHOVEL: 299,

  LEATHER_HELM: 310,
  LEATHER_CHEST: 311,
  LEATHER_LEGS: 312,
  LEATHER_BOOTS: 313,
  IRON_HELM: 314,
  IRON_CHEST: 315,
  IRON_LEGS: 316,
  IRON_BOOTS: 317,
  GOLD_HELM: 318,
  GOLD_CHEST: 319,
  GOLD_LEGS: 320,
  GOLD_BOOTS: 321,
  DIAMOND_HELM: 322,
  DIAMOND_CHEST: 323,
  DIAMOND_LEGS: 324,
  DIAMOND_BOOTS: 325,
} as const;

export type ItemId = number;
export type ArmorSlot = "head" | "chest" | "legs" | "feet";

export interface ItemDef {
  id: number;
  name: string;
  stack: number;
  place?: number;
  tool?: ToolType;
  power?: number;
  durability?: number;
  armor?: number;
  armorSlot?: ArmorSlot;
  food?: { hunger: number; heal: number };
  fuel?: number;
  color: [number, number, number];
  icon: "block" | "ingot" | "gem" | "stick" | "food" | "tool" | "armor" | "orb";
}

const ITEMS: Record<number, ItemDef> = {};

function add(def: ItemDef): void {
  ITEMS[def.id] = def;
}

function tool(
  id: number,
  name: string,
  toolType: ToolType,
  power: number,
  durability: number,
  color: [number, number, number],
): void {
  add({
    id,
    name,
    stack: 1,
    tool: toolType,
    power,
    durability,
    color,
    icon: "tool",
  });
}

function armor(
  id: number,
  name: string,
  slot: ArmorSlot,
  points: number,
  color: [number, number, number],
): void {
  add({
    id,
    name,
    stack: 1,
    armor: points,
    armorSlot: slot,
    durability: 80 + points * 18,
    color,
    icon: "armor",
  });
}

export function registerBlockItems(
  blocks: Record<number, { id: number; name: string }>,
): void {
  for (const b of Object.values(blocks)) {
    if (b.id === 0) continue;
    if (ITEMS[b.id]) continue;
    add({
      id: b.id,
      name: b.name,
      stack: 64,
      place: b.id,
      color: [180, 180, 180],
      icon: "block",
    });
  }
}

add({
  id: Item.COAL,
  name: "Coal",
  stack: 64,
  fuel: 8,
  color: [32, 32, 36],
  icon: "orb",
});
add({
  id: Item.IRON_INGOT,
  name: "Iron Ingot",
  stack: 64,
  color: [198, 204, 210],
  icon: "ingot",
});
add({
  id: Item.GOLD_INGOT,
  name: "Gold Ingot",
  stack: 64,
  color: [240, 196, 72],
  icon: "ingot",
});
add({
  id: Item.DIAMOND,
  name: "Diamond",
  stack: 64,
  color: [72, 220, 214],
  icon: "gem",
});
add({
  id: Item.WHEAT,
  name: "Wheat",
  stack: 64,
  color: [214, 180, 72],
  icon: "food",
});
add({
  id: Item.APPLE,
  name: "Apple",
  stack: 64,
  food: { hunger: 4, heal: 1 },
  color: [196, 48, 48],
  icon: "food",
});
add({
  id: Item.BREAD,
  name: "Bread",
  stack: 64,
  food: { hunger: 6, heal: 1 },
  color: [196, 148, 72],
  icon: "food",
});
add({
  id: Item.STICK,
  name: "Stick",
  stack: 64,
  fuel: 1,
  color: [132, 92, 48],
  icon: "stick",
});
add({
  id: Item.LEATHER,
  name: "Leather",
  stack: 64,
  color: [140, 88, 52],
  icon: "orb",
});
add({
  id: Item.RAW_PORK,
  name: "Raw Pork",
  stack: 64,
  food: { hunger: 2, heal: 0 },
  color: [214, 132, 132],
  icon: "food",
});
add({
  id: Item.COOKED_PORK,
  name: "Cooked Pork",
  stack: 64,
  food: { hunger: 8, heal: 2 },
  color: [164, 88, 52],
  icon: "food",
});
add({
  id: Item.SLIME_GEL,
  name: "Slime Gel",
  stack: 64,
  color: [96, 196, 72],
  icon: "orb",
});

const wood: [number, number, number] = [166, 124, 68];
const stoneC: [number, number, number] = [140, 140, 144];
const ironC: [number, number, number] = [198, 204, 210];
const goldC: [number, number, number] = [240, 196, 72];
const diaC: [number, number, number] = [72, 220, 214];
const leatherC: [number, number, number] = [140, 88, 52];

tool(Item.WOOD_SWORD, "Wooden Sword", "sword", 4, 60, wood);
tool(Item.WOOD_PICK, "Wooden Pickaxe", "pickaxe", 2, 60, wood);
tool(Item.WOOD_AXE, "Wooden Axe", "axe", 2, 60, wood);
tool(Item.WOOD_SHOVEL, "Wooden Shovel", "shovel", 2, 60, wood);
tool(Item.STONE_SWORD, "Stone Sword", "sword", 5, 132, stoneC);
tool(Item.STONE_PICK, "Stone Pickaxe", "pickaxe", 4, 132, stoneC);
tool(Item.STONE_AXE, "Stone Axe", "axe", 4, 132, stoneC);
tool(Item.STONE_SHOVEL, "Stone Shovel", "shovel", 4, 132, stoneC);
tool(Item.IRON_SWORD, "Iron Sword", "sword", 6, 250, ironC);
tool(Item.IRON_PICK, "Iron Pickaxe", "pickaxe", 6, 250, ironC);
tool(Item.IRON_AXE, "Iron Axe", "axe", 6, 250, ironC);
tool(Item.IRON_SHOVEL, "Iron Shovel", "shovel", 6, 250, ironC);
tool(Item.GOLD_SWORD, "Golden Sword", "sword", 4, 40, goldC);
tool(Item.GOLD_PICK, "Golden Pickaxe", "pickaxe", 8, 40, goldC);
tool(Item.GOLD_AXE, "Golden Axe", "axe", 8, 40, goldC);
tool(Item.GOLD_SHOVEL, "Golden Shovel", "shovel", 8, 40, goldC);
tool(Item.DIAMOND_SWORD, "Diamond Sword", "sword", 7, 1560, diaC);
tool(Item.DIAMOND_PICK, "Diamond Pickaxe", "pickaxe", 8, 1560, diaC);
tool(Item.DIAMOND_AXE, "Diamond Axe", "axe", 8, 1560, diaC);
tool(Item.DIAMOND_SHOVEL, "Diamond Shovel", "shovel", 8, 1560, diaC);

armor(Item.LEATHER_HELM, "Leather Cap", "head", 1, leatherC);
armor(Item.LEATHER_CHEST, "Leather Tunic", "chest", 3, leatherC);
armor(Item.LEATHER_LEGS, "Leather Pants", "legs", 2, leatherC);
armor(Item.LEATHER_BOOTS, "Leather Boots", "feet", 1, leatherC);
armor(Item.IRON_HELM, "Iron Helmet", "head", 2, ironC);
armor(Item.IRON_CHEST, "Iron Chestplate", "chest", 6, ironC);
armor(Item.IRON_LEGS, "Iron Leggings", "legs", 5, ironC);
armor(Item.IRON_BOOTS, "Iron Boots", "feet", 2, ironC);
armor(Item.GOLD_HELM, "Golden Helmet", "head", 2, goldC);
armor(Item.GOLD_CHEST, "Golden Chestplate", "chest", 5, goldC);
armor(Item.GOLD_LEGS, "Golden Leggings", "legs", 3, goldC);
armor(Item.GOLD_BOOTS, "Golden Boots", "feet", 1, goldC);
armor(Item.DIAMOND_HELM, "Diamond Helmet", "head", 3, diaC);
armor(Item.DIAMOND_CHEST, "Diamond Chestplate", "chest", 8, diaC);
armor(Item.DIAMOND_LEGS, "Diamond Leggings", "legs", 6, diaC);
armor(Item.DIAMOND_BOOTS, "Diamond Boots", "feet", 3, diaC);

export function itemDef(id: number): ItemDef {
  const found = ITEMS[id];
  if (found) return found;
  const b = blockDef(id);
  return {
    id,
    name: b.name,
    stack: 64,
    place: id === 0 ? undefined : id,
    color: [180, 180, 180],
    icon: "block",
  };
}

export function isBlockItem(id: number): boolean {
  return id > 0 && id < 256;
}

export function maxStack(id: number): number {
  return itemDef(id).stack;
}

export function toolPower(id: number, kind: ToolType): number {
  const d = itemDef(id);
  if (d.tool !== kind) return 1;
  return d.power ?? 1;
}

export function breakSpeed(blockId: number, toolId: number | null): number {
  const b = blockDef(blockId);
  if (b.hardness < 0) return 0;
  if (b.hardness === 0) return 20;
  let power = 1;
  if (toolId !== null) {
    const t = itemDef(toolId);
    if (t.tool === b.tool) power = t.power ?? 1;
    else if (t.tool === "sword" && b.plant) power = 4;
  }
  return power / b.hardness;
}

export function canHarvest(blockId: number, toolId: number | null): boolean {
  const b = blockDef(blockId);
  if (b.tool === "none" || b.tool === "shovel" || b.tool === "axe" || b.tool === "sword") {
    return true;
  }
  if (b.tool === "pickaxe") {
    if (toolId === null) return false;
    return itemDef(toolId).tool === "pickaxe";
  }
  return true;
}

export { ITEMS };
